/* eslint-disable camelcase */
/**
 * Resolve a line of free text into a Google `PlaceResult`, the same way the
 * address box does when a visitor picks a suggestion themselves.
 *
 * This exists for the marketing-site hand-off. The site can only send free
 * text when it has no address provider of its own (Google blocked, or its key
 * does not list that host yet - see aperio/site/src/static/js/addr.js), and the
 * owner's requirement for that case is that the visitor still does not retype
 * their address here: we look the text up, take the first prediction, and put
 * the result into the store exactly as `SearchLocationInput` would.
 *
 * Deliberate properties:
 * - It never throws and never rejects. Every failure - no Places library, no
 *   predictions, a denied key, a place with no postal code - resolves
 *   `undefined`, and the caller falls back to letting the visitor pick.
 * - It asks util/mapsLoader for the Places library - the same loader the
 *   address box uses, so the page keeps exactly one Maps script and one API
 *   key - and waits for it. The box no longer loads Maps as it mounts (QA
 *   2026-09-25 #14), so this is what brings it in for a hand-off.
 * - Same restrictions as the input it stands in for: `types: ['address']`,
 *   `componentRestrictions: { country: ['us', 'ca'] }`, and the same `fields`,
 *   so the resulting place maps through `placeResultToAddress` unchanged.
 * - One `AutocompleteSessionToken` covers the prediction and the details call,
 *   which is how Google bills a session.
 */

import { loadPlaces } from './mapsLoader';

export type PlaceResult = google.maps.places.PlaceResult;

/** Fields of the PlaceResult we need; identical to SearchLocationInput's. */
const FIELDS = [
  'place_id',
  'formatted_address',
  'address_components',
  'geometry',
];
const COUNTRIES = ['us', 'ca'];

/** How long to wait for the Maps script the address input is loading. */
export const PLACES_WAIT_MS = 8000;
const POLL_MS = 100;

type PlacesLib = typeof google.maps.places;

function placesLib(): PlacesLib | null {
  const maps = (window as unknown as { google?: typeof google }).google?.maps;
  return maps && maps.places ? maps.places : null;
}

/**
 * Resolve once the Places library is on the page, or with `null` when it never
 * arrives (script blocked, offline, or simply no API key configured).
 */
export function waitForPlaces(
  timeoutMs = PLACES_WAIT_MS,
  language?: string | null,
): Promise<PlacesLib | null> {
  const ready = placesLib();
  if (ready) {
    return Promise.resolve(ready);
  }
  // Ask for it (a no-op when it is already on its way or cannot be loaded).
  loadPlaces(language);
  return new Promise((resolve) => {
    const started = Date.now();
    const timer = setInterval(() => {
      const lib = placesLib();
      if (lib || Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(lib);
      }
    }, POLL_MS);
  });
}

function hasComponent(place: PlaceResult | null, type: string): boolean {
  return !!place?.address_components?.some((c) => c.types.includes(type));
}

function firstPrediction(
  lib: PlacesLib,
  text: string,
  token: google.maps.places.AutocompleteSessionToken,
): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      new lib.AutocompleteService().getPlacePredictions(
        {
          input: text,
          types: ['address'],
          componentRestrictions: { country: COUNTRIES },
          sessionToken: token,
        },
        (predictions, status) => {
          const first = status === 'OK' && predictions && predictions.length
            ? predictions[0]
            : null;
          resolve(first ? first.place_id : null);
        },
      );
    } catch (e) {
      // An invalid request or a library that is not what we think it is.
      resolve(null);
    }
  });
}

function details(
  lib: PlacesLib,
  placeId: string,
  token: google.maps.places.AutocompleteSessionToken,
): Promise<PlaceResult | null> {
  return new Promise((resolve) => {
    try {
      // PlacesService needs an element (or a map) to attribute results to; an
      // unattached div is what Google's own examples use for a headless lookup.
      new lib.PlacesService(document.createElement('div')).getDetails(
        { placeId, fields: FIELDS, sessionToken: token },
        (place, status) => resolve(status === 'OK' ? place : null),
      );
    } catch (e) {
      resolve(null);
    }
  });
}

/** The house number a line of text starts with: "555" of "555 5th St". */
export function leadingHouseNumber(text?: string | null): string | null {
  const match = /^\s*(\d{1,6}[A-Za-z]?)(?:[\s,]|$)/.exec(text || '');
  return match ? match[1] : null;
}

function componentName(place: PlaceResult | null, type: string): string {
  return place?.address_components
    ?.find((c) => c.types.includes(type))?.long_name || '';
}

/**
 * The visitor picked a numbered suggestion ("555 5th St, Toronto") but
 * Google's details came back for the street alone - no street number, often
 * no postal code - and the house number vanished from the box (QA
 * 2026-09-25 #24). Ask again for "<number> <that street>" and take the
 * answer only if it is the same street with that number and a postal code:
 * the number is confirmed by Google, never made up here. Resolves
 * `undefined` otherwise (the caller then asks the visitor to pick again).
 */
export async function resolveNumberedAddress(
  number: string,
  streetPlace: PlaceResult,
  timeoutMs = PLACES_WAIT_MS,
): Promise<PlaceResult | undefined> {
  const route = componentName(streetPlace, 'route');
  const base = streetPlace.formatted_address || '';
  if (!number || !route || !base) return undefined;
  const lib = await waitForPlaces(timeoutMs);
  if (!lib) return undefined;
  try {
    const token = new lib.AutocompleteSessionToken();
    const placeId = await firstPrediction(lib, `${number} ${base}`, token);
    if (!placeId) return undefined;
    const place = await details(lib, placeId, token);
    if (!place?.address_components) return undefined;
    const sameStreet = componentName(place, 'route').toLowerCase()
      === route.toLowerCase();
    const sameNumber = componentName(place, 'street_number').toLowerCase()
      === number.toLowerCase();
    if (!sameStreet || !sameNumber || !hasComponent(place, 'postal_code')) {
      return undefined;
    }
    return place;
  } catch (e) {
    return undefined;
  }
}

/**
 * The address the visitor typed on the marketing site, resolved to a place.
 *
 * Resolves `undefined` when there is nothing we can safely use - which here
 * includes a place with no postal code (a city, or a street with no number):
 * the booking flow is driven by the postal code, so a place without one would
 * auto-advance into a service-area check that cannot be answered.
 */
export default async function resolveAddressText(
  text: string,
  timeoutMs = PLACES_WAIT_MS,
  language?: string | null,
): Promise<PlaceResult | undefined> {
  const query = (text || '').trim();
  if (!query) {
    return undefined;
  }
  const lib = await waitForPlaces(timeoutMs, language);
  if (!lib) {
    return undefined;
  }
  try {
    const token = new lib.AutocompleteSessionToken();
    const placeId = await firstPrediction(lib, query, token);
    if (!placeId) {
      return undefined;
    }
    const place = await details(lib, placeId, token);
    if (!place || !place.address_components) {
      return undefined;
    }
    if (!hasComponent(place, 'postal_code')) {
      return undefined;
    }
    return place;
  } catch (e) {
    return undefined;
  }
}
